import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import {
  fetcher,
  mutateRequest,
  optimisticList,
  optimisticPrd,
  postFetcher,
  putFetcher,
  putFetcherKeyed,
} from "@/lib/swr";
import type { VersionReportDoc } from "@/types/domain";

export type ReportOwnerType = "prd" | "version";

/** Owner PUT response — only the fields this hook actually reads. */
type OwnerPutResult = {
  reportDocs?: VersionReportDoc[];
  webhookResult?: { success?: boolean; error?: string };
};

const ownerBase = (ownerType: ReportOwnerType, ownerId: string) =>
  ownerType === "prd" ? `/prds/${ownerId}` : `/versions/${ownerId}`;

/**
 * Shared report-doc operations for the three report panels (PRD cell, engagement
 * cell, version timeline): add / delete (with confirm state), summary
 * editing (find-by-url fallback + PUT /reports/:id), and webhook resend.
 * The owner-specific cache writes are injected via `applyDocs` / `refresh`.
 */
export const useReportDocs = ({
  ownerType,
  ownerId,
  reportDocs,
  applyDocs,
  refresh,
  onMutationSuccess,
  onMutationError,
}: {
  ownerType: ReportOwnerType;
  ownerId: string;
  /** Current docs list of the owner (the retained list is derived from it). */
  reportDocs: VersionReportDoc[];
  /** Optimistic cache write of the new docs array (list + atom keys). */
  applyDocs: (docs: VersionReportDoc[]) => void;
  /** Revalidate the owner detail after a summary save. */
  refresh?: () => void;
  /** Extra side effects after the owner PUT succeeds (close dialogs…). */
  onMutationSuccess?: () => void;
  /** Extra side effects on owner PUT error (surface submit error…). */
  onMutationError?: (message: string) => void;
}) => {
  const { toast } = useToast();
  const [deleteUrl, setDeleteUrl] = useState<string | null>(null);
  const [editingDocUrl, setEditingDocUrl] = useState<string | null>(null);
  const [editingSummary, setEditingSummary] = useState("");
  const [isSavingSummary, setIsSavingSummary] = useState(false);
  const [resendingDocUrl, setResendingDocUrl] = useState<string | null>(null);

  // `#report-docs` isolates isMutating from other mutations on the same URL.
  const { trigger, isMutating } = useSWRMutation(
    `${ownerBase(ownerType, ownerId)}#report-docs`,
    putFetcherKeyed<OwnerPutResult>,
    {
      onSuccess: (data) => {
        if (data?.reportDocs) applyDocs(data.reportDocs);
        // Don't revalidate immediately to avoid a blank page — the caches are
        // already updated optimistically.
        onMutationSuccess?.();
      },
      onError: (error) => {
        const message =
          error?.message || "An error occurred while processing the report.";
        onMutationError?.(message);
        toastError(toast, "Operation failed", error, message);
      },
    }
  );

  /** Submit one new report doc (PUT { reportDoc }); warns when the Feishu
   * webhook failed even though the report itself was saved. */
  const submitDoc = async (doc: Partial<VersionReportDoc>) => {
    const result = await trigger({ reportDoc: doc });
    toast({ title: "Success", description: "Report added." });
    if (result?.webhookResult && !result.webhookResult.success) {
      toast({
        title: "Report saved, but the Feishu notification failed to send",
        description:
          result.webhookResult.error ||
          "Please send the message manually in Feishu",
      });
    }
    return result;
  };

  // ---- delete (confirm dialog keyed by doc url) ----
  const askDelete = (url: string | null) => setDeleteUrl(url);

  const confirmDelete = async () => {
    if (deleteUrl == null) return;
    const newDocs = reportDocs.filter((d) => d.url !== deleteUrl);
    try {
      await trigger({ reportDocs: newDocs });
      applyDocs(newDocs);
      toast({ title: "Deleted", description: "Report deleted." });
      setDeleteUrl(null);
    } catch {
      // Already surfaced by the mutation's onError.
    }
  };

  // ---- summary editing ----
  const startEditSummary = (doc: VersionReportDoc) => {
    setEditingDocUrl(doc.url);
    setEditingSummary(doc.summary || "");
  };

  const cancelEditSummary = () => {
    setEditingDocUrl(null);
    setEditingSummary("");
  };

  const saveSummary = async (doc: VersionReportDoc) => {
    setIsSavingSummary(true);
    try {
      let reportDocId = doc.id;
      // Docs saved before /reports existed carry no id — look it up by url.
      if (!reportDocId) {
        try {
          const found = await fetcher<{ id?: string }>([
            "/reports/find-by-url",
            {
              ownerType,
              ownerId,
              url: doc.url,
              versionId: ownerType === "prd" ? doc.versionId : undefined,
            },
          ]);
          if (found?.id) reportDocId = found.id as string;
        } catch {
          // Fall through to the explicit error below.
        }
      }
      if (!reportDocId) {
        throw new Error(
          "Could not get the report document ID, please refresh the page and try again"
        );
      }
      await putFetcher(`/reports/${reportDocId}`, {
        arg: { summary: editingSummary },
      });
      const updatedDocs = reportDocs.map((d) =>
        d.url === doc.url &&
        (ownerType === "version" || d.versionId === doc.versionId)
          ? { ...d, summary: editingSummary, id: reportDocId }
          : d
      );
      applyDocs(updatedDocs);
      refresh?.();
      toast({ title: "Saved", description: "Summary updated." });
      setEditingDocUrl(null);
    } catch (error) {
      toastError(
        toast,
        "Save failed",
        error,
        "An error occurred while updating the summary."
      );
    } finally {
      setIsSavingSummary(false);
    }
  };

  // ---- Feishu webhook resend ----
  const resendWebhook = async (docUrl: string) => {
    setResendingDocUrl(docUrl);
    try {
      const result = await postFetcher<{ success?: boolean; error?: string }>(
        `${ownerBase(ownerType, ownerId)}/report-docs/${encodeURIComponent(
          docUrl
        )}/resend-webhook`,
        { arg: {} }
      );
      if (result?.success) {
        toast({ title: "Resent", description: "Feishu message resent." });
      } else {
        toastError(
          toast,
          "Resend failed",
          { message: result?.error },
          "An error occurred while resending the Feishu message."
        );
      }
    } catch (error) {
      toastError(
        toast,
        "Resend failed",
        error,
        "An error occurred while resending the Feishu message."
      );
    } finally {
      setResendingDocUrl(null);
    }
  };

  return {
    trigger,
    isMutating,
    submitDoc,
    deleteUrl,
    askDelete,
    confirmDelete,
    editingDocUrl,
    editingSummary,
    setEditingSummary,
    startEditSummary,
    cancelEditSummary,
    isSavingSummary,
    saveSummary,
    resendingDocUrl,
    resendWebhook,
  };
};

export type ReportDocsApi = ReturnType<typeof useReportDocs>;

/**
 * PRD-owner convenience wrapper: the cache wiring (optimistic list + atom
 * write, detail revalidation) is identical wherever a PRD's reports are
 * managed — inject only the extra dialog side effects when needed.
 */
export const usePrdReportDocs = (
  prdId: string,
  reportDocs: VersionReportDoc[],
  extra?: {
    onMutationSuccess?: () => void;
    onMutationError?: (message: string) => void;
  },
) =>
  useReportDocs({
    ownerType: "prd",
    ownerId: prdId,
    reportDocs,
    applyDocs: (next) => {
      optimisticList("/prds", prdId, { reportDocs: next });
      optimisticPrd(prdId, { reportDocs: next });
    },
    refresh: () => mutateRequest(`/prds/${prdId}`),
    ...extra,
  });
